<script setup lang="ts">import { computed as _computed } from 'vue';

import moment from "moment";
import { useRouter } from "vue-router";
import { computed, ref, watch } from "vue";
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from "reka-ui";

import { downloadFile } from "@recordsure/unity-stk";

import { authMachine } from "@/machines/auth-machine";

import { metadataService } from "@/services";
import { $can, copyToClipboard, isNumber, toMoment, trimEnd } from "@/services/UtilitiesService";

import { config } from "@/config";

import { Metadata } from "@/models/Metadata";

import { useIoaStore } from "@/stores/ioa-store";
import { useNotesStore } from "@/stores/notes-store";
import { useBrandStore } from "@/stores/brand-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useCaseFileStore } from "@/stores/case-file-store";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import RsManageNotes from "./ManageNotes.vue";
import RsManageIdentities from "./ManageIdentities.vue";
import ConfirmPopover from "@/components/ConfirmPopover.vue";
import StringTooltip from "@/components/Tooltip/StringTooltip.vue";
import RecordSHAVerification from "@/components/CasePanels/RecordDetails/RecordDetailsSHAVerification.vue";
import { RsRecordSearch, RsManageTags, RsCheckbox } from "@/components";

import type { Note } from "@/models/Note";
import type { DockviewPanelComponentProps, DockviewPanelComponentEmits } from "@/hooks/use-case-panel";

defineProps<DockviewPanelComponentProps<false>>();
defineEmits<DockviewPanelComponentEmits>();

const $router = useRouter();
const routeParams = useCaseRouteParams();

const form = ref({
  edxIssueDate: "",
  edxIssueDateMessage: "",
  aiClassification: "",
  aiClassificationGroup: "",
  isDuplicate: false,
  isDraft: false,
});

const propertiesConfig = _computed(() => {
  const properties = useBrandStore().viewerConfig.recordDetails ?? useBrandStore().viewerConfig.properties ?? {};
  return properties;
});

const ioaConfig = _computed(() => {
  return {
    label: "Instance of Advice",
    disabled: false,
    ...(propertiesConfig.value?.ioa || {}),
  };
});

const canAnnotate = _computed(() => {
  return $can("annotate");
});

const canClassify = _computed(() => {
  return $can("classify");
});

const item = _computed(() => useCaseFileStore().currentFile);

const extendedMetadata = computed(() => {
  return useCaseFileStore().fileExtendedMetadata;
});

const documentClassificationsGroup = _computed(() => {
  return Object.keys(useBrandStore().classifications).sort();
});

const documentClassifications = _computed(() => {
  const classifications = useBrandStore().classifications[form.value.aiClassificationGroup]?.subClassifications ?? [];
  return classifications.map((c) => c.name).sort();
});

const notes = _computed(() => {
  if (!item.value) {
    return [];
  }

  const n: readonly Note[] = useNotesStore().notesObject[item.value.original];

  return n || [];
});

const validateDate = _computed(() => {
  form.value.edxIssueDateMessage = "";
  if (form.value.edxIssueDate === "") {
    return "";
  }
  let format = "YYYY MM DD";
  switch (form.value.edxIssueDate.split(" ").length) {
    case 1:
      format = "YYYY";
      break;
    case 2:
      format = "YYYY MM";
      break;
    default:
      format = "YYYY MM DD";
      break;
  }

  const momentInstance = moment(form.value.edxIssueDate, format, true);

  if (!momentInstance.isValid()) {
    form.value.edxIssueDateMessage = `Date should be in 'YYYY' or 'YYYY MM' or 'YYYY MM DD' format`;
    return "tw-is-input-invalid";
  }

  if (momentInstance.isAfter(moment())) {
    form.value.edxIssueDateMessage = `Date cannot be greater than ${moment().format("YYYY MM DD")}`;
    return "tw-is-input-invalid";
  }

  if (momentInstance.isBefore(moment("1900 01 01", "YYYY MM DD", true))) {
    form.value.edxIssueDateMessage = `Date cannot be earlier than ${moment("1900 01 01", "YYYY MM DD", true).format(
      "YYYY MM DD",
    )}`;
    return "tw-is-input-invalid";
  }

  return "";
});

const hasOfficeProperties = _computed(() => {
  return Object.keys(extendedMetadata.value?.officeProperties ?? {}).length > 0;
});

const hasOfficeCustomProperties = _computed(() => {
  return Object.keys(extendedMetadata.value?.officeCustomProperties ?? {}).length > 0;
});

const showIdentities = _computed(() => {
  const { fileTranscript, formOnlyRecording } = useCaseFileStore();

  return (
    useBrandStore().featuresConfig.transcript !== "disabled" &&
    fileTranscript?.status === "success" &&
    !formOnlyRecording &&
    fileTranscript.speakers.length > 0
  );
});

const ingestProcessingStatus = _computed(() => {
  return (
    item.value &&
    item.value.edxProcessingStatus &&
    item.value.edxProcessingStatus === "issue" &&
    item.value.ingestProcessingError &&
    item.value.ingestProcessingStatus
  );
});

const ioas = _computed(() => {
  return useIoaStore().instanceOfAdvices;
});

const ioaCurrentItem = _computed(() => {
  if (!item.value) return -1;
  return ioas.value.findIndex((i) => i.fileReferences.includes(item.value.fileReference));
});

const ioaStatus = _computed(() => {
  return useIoaStore().moduleStatus;
});

const ioaOptions = _computed(() => {
  if (!item.value) return [];
  const date = toMoment(item.value.edxIssueDate || "", true);

  if (!date) return [];

  // Check if item is within any existing IoA
  let index = ioas.value.findIndex((i) => date.toDate() >= i.startDate! && date.toDate() <= i.endDate!);
  if (index > -1) {
    return [
      {
        instanceReference: ioas.value[index].instanceReference,
        message: `Add to instance #${index + 1}`,
      },
    ];
  }

  const options = [{ instanceReference: "", message: "Add to new instance" }];

  // Check if item is before any existing IoA
  index = ioas.value.findIndex((i) => date.toDate() < i.startDate!);
  if (index > -1) {
    options.push({
      instanceReference: ioas.value[index].instanceReference,
      message: `Add to instance #${index + 1}`,
    });
  }

  // Check if item is after any existing IoA. Array is reversed to get last occurrence.
  index = [...ioas.value].reverse().findIndex((i) => date.toDate() > i.endDate!);
  if (index > -1) {
    const invertedIndex = ioas.value.length - index;
    options.push({
      instanceReference: ioas.value[invertedIndex - 1].instanceReference,
      message: `Add to instance #${invertedIndex}`, // Because the array is reversed
    });
  }

  return options;
});

const currentUrl = _computed(() => {
  if (routeParams.value.casefile == null) return "";
  return String.raw`${window.location.protocol}//${window.location.host}/${routeParams.value.brand}/${routeParams.value.casename}/${routeParams.value.casefile}`;
});

const downloadState = _computed(() => {
  const metadata = useCaseFileStore().fileMetadata?.data;

  if (metadata?.session) {
    return "Form";
  }

  if (metadata?.audio) {
    return "Audio";
  }
  return "File";
});

const tabs = computed<Array<{ key: string; title: string; hide?: boolean }>>(() => [
  { key: "edit", title: "Properties" },
  { key: "notes", title: `Notes (${notes.value.length})` },
  { key: "officeProperties", title: "Office Properties", hide: !hasOfficeProperties.value },
  { key: "officeCustomProperties", title: "Office Custom Properties", hide: !hasOfficeCustomProperties.value },
  { key: "identities", title: "Identities", hide: !showIdentities.value },
  { key: "sha", title: "Authenticity" },
]);

const selectedTab = ref(tabs.value[0].key);

watch(
  extendedMetadata,
  () => {
    if (extendedMetadata.value == null) return;
    loadProperties();
  },
  { immediate: true },
);

// TODO there is no isClosed ?!
// watch($$(isClosed),
// () => {
//   onFileChange();
// })

function onClassificationGroupChanged(): Promise<void> {
  if (documentClassifications.value.length === 1) {
    form.value.aiClassification = documentClassifications.value[0];
    return onClassificationChanged();
  }
  return Promise.resolve();
}

function onClassificationChanged(): Promise<void> {
  if (!form.value.aiClassification) {
    return Promise.resolve();
  }

  return useMetadataStore().updateMetadata({
    property: "aiClassification",
    value: form.value.aiClassification,
  });
}

function loadProperties() {
  resetForm();
  if (!item.value) {
    return;
  }

  selectedTab.value = tabs.value[0].key;
  form.value = {
    edxIssueDate: item.value.edxIssueDate ?? "",
    aiClassification: item.value.aiClassification || "",
    aiClassificationGroup: item.value.aiClassificationGroup || "",
    edxIssueDateMessage: form.value.edxIssueDateMessage,
    isDraft: String(item.value.isDraft).toLowerCase() === "true",
    isDuplicate: String(item.value.isDuplicate).toLowerCase() === "true",
  };
}

function downloadEarliest(itemToDownload: Metadata) {
  let fileReference: string | undefined;

  if (itemToDownload.fileType !== "ac5") fileReference = itemToDownload.fileReference;
  else
    fileReference = useMetadataStore().files.find(
      (f) => f.parent === itemToDownload.fileReference && f.relation === "EmbeddedAudio",
    )?.fileReference;

  if (fileReference == null) return;

  const { client, brand, casename } = routeParams.value;
  const controller = new AbortController();
  const token = authMachine.getSnapshot().context.token?.access_token;

  if (token == null) return;

  downloadFile(
    `${config.apiUrl}/api/binary/Original/${client}/${brand}/${encodeURIComponent(casename)}/${fileReference}`,
    { accessToken: token, ssoApiUrl: config.ssoApiUrl, signal: controller.signal },
  );
}

async function removeIssueDate(): Promise<void> {
  if (routeParams.value.casefile == null) return;

  // Delete associated IoA as well
  await deleteFromIoA();

  await metadataService.deleteProperty(
    routeParams.value.client,
    routeParams.value.brand,
    routeParams.value.casename,
    routeParams.value.casefile,
    "edxIssueDate",
  );
  await useMetadataStore().refresh();

  form.value.edxIssueDate = "";
}

async function onDateTimeSelected(): Promise<void> {
  if (routeParams.value.casefile == null) return;

  if (validateDate.value !== "") {
    return Promise.resolve();
  }

  // Delete associated IoA as well
  await deleteFromIoA();

  await metadataService.saveProperty(
    routeParams.value.client,
    routeParams.value.brand,
    routeParams.value.casename,
    routeParams.value.casefile,
    "edxIssueDate",
    { edxIssueDate: form.value.edxIssueDate },
  );
  await useMetadataStore().refresh();
}

async function onDraftChange(isDraft: boolean): Promise<void> {
  if (routeParams.value.casefile == null) return;

  await metadataService
    .saveProperty(
      routeParams.value.client,
      routeParams.value.brand,
      routeParams.value.casename,
      routeParams.value.casefile,
      "isDraft",
      { isDraft },
    )
    .then(() => useMetadataStore().refresh());
}

async function addToIoA(instanceReference: string) {
  await useIoaStore().add({ instanceReference });
}

async function deleteFromIoA() {
  if (ioaCurrentItem.value < 0) return;
  await useIoaStore().remove({
    instanceReference: ioas.value[ioaCurrentItem.value].instanceReference,
  });
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getUrl(version: string): string {
  if (routeParams.value.casefile == null) return "";

  return `${trimEnd(document.getElementsByTagName("base")[0].href, "/")}/api/binary/${version}/${
    routeParams.value.client
  }/${routeParams.value.brand}/${encodeURIComponent(routeParams.value.casename)}/${routeParams.value.casefile}`;
}

function resetForm() {
  form.value = {
    edxIssueDate: "",
    edxIssueDateMessage: "",
    aiClassification: "",
    aiClassificationGroup: "",
    isDuplicate: false,
    isDraft: false,
  };
}

function viewAudit(): void {
  if (routeParams.value.casefile == null) return;

  $router.push({
    name: "fileaudit",
    params: {
      brand: routeParams.value.brand,
      casename: routeParams.value.casename,
      casefile: routeParams.value.casefile,
    },
  });
}
</script>
<template>
  <div class="tw-flex tw-size-full tw-flex-col tw-overflow-y-auto">
    <div v-if="item != null" class="metadata tw-flex tw-h-full tw-flex-col">
      <RsRecordSearch
        v-if="useBrandStore().viewerConfig.recordList?.showSearch"
        :brand="routeParams.brand"
        :case-reference="routeParams.casename"
        class="tw-flex-no-wrap tw-mx-2 tw-my-1 tw-flex tw-items-center"
      />
      <TabsRoot v-model="selectedTab" class="tw-flex tw-h-full tw-flex-col">
        <div v-if="ingestProcessingStatus" class="tw-mx-2 tw-my-1">
          <strong>Ingest Processing Status -</strong>
          {{ ingestProcessingStatus }}
        </div>
        <TabsList class="tw-mt-1 tw-border-b tw-px-2">
          <div class="tw-flex tw-gap-1 tw-overflow-x-auto">
            <template v-for="tab in tabs" :key="tab.key">
              <TabsTrigger
                v-if="!tab.hide"
                :key="tab.key"
                class="tw-min-w-max tw-rounded-t-md tw-border tw-border-b-0 tw-px-2 tw-py-1 hover:tw-brightness-90"
                :value="tab.key"
                :class="selectedTab === tab.key ? 'tw-bg-green' : 'tw-bg-white'"
              >
                {{ tab.title }}
              </TabsTrigger>
            </template>
          </div>
        </TabsList>
        <TabsContent as-child value="notes">
          <RsManageNotes :item="item" />
        </TabsContent>
        <!-- Editable properties should go after here -->
        <TabsContent as-child value="edit">
          <div class="tw-flex tw-size-full tw-grow tw-flex-wrap tw-overflow-auto tw-bg-gray-100 tw-p-2">
            <div class="tw-mb-2 tw-mr-3 tw-bg-white tw-px-2 tw-pt-2 tw-shadow">
              <div class="tw-mb-1 tw-grid tw-grid-cols-1">
                <label for="fileReference"> File Reference </label>
                <div class="tw-flex tw-items-center">
                  <StringTooltip side="top" :item="item.fileReference">
                    <input
                      id="fileReference"
                      readonly
                      type="text"
                      class="tw-form-input tw-mr-2"
                      :value="item.fileReference"
                    />
                  </StringTooltip>
                  <StringTooltip v-if="currentUrl" side="top" item="Copy File URL to Clipboard" #="{ item }">
                    <button
                      type="button"
                      class="tw-p-1"
                      :aria-label="item"
                      @click.prevent="copyToClipboard(currentUrl, 'Url copied to clipboard')"
                    >
                      <i aria-hidden="true" class="far fa-copy mt-2" />
                    </button>
                  </StringTooltip>
                </div>
              </div>
              <div v-if="(item.edxCreatedOn || '').length > 0" class="tw-mb-1 tw-grid tw-grid-cols-1">
                <label> Uploaded On </label>
                <p class="tw-b-r tw-bg-gray-300 tw-px-2 tw-py-1 tw-leading-6">
                  {{ item.edxCreatedOn }}
                </p>
              </div>
              <form @submit.prevent>
                <div class="tw-mb-1 tw-grid tw-grid-cols-1">
                  <label for="issueDate">
                    Issue Date
                    <small class="tw-text-[#636463]">(YYYY MM DD)</small>
                  </label>
                  <div class="tw-relative">
                    <p v-if="!canAnnotate" class="tw-b-r tw-px-2 tw-py-1 tw-leading-6">
                      {{ form.edxIssueDate }}
                    </p>
                    <template v-else>
                      <ConfirmPopover
                        v-if="ioaCurrentItem > -1"
                        trigger="manual"
                        confirm-text="Proceed"
                        @confirmed="onDateTimeSelected"
                        @canceled="form.edxIssueDate = item.edxIssueDate ?? ''"
                      >
                        <template #trigger="{ open }">
                          <input
                            id="issueDate"
                            v-model.trim="form.edxIssueDate"
                            type="text"
                            maxlength="10"
                            class="tw-form-input"
                            placeholder="YYYY MM DD"
                            :class="validateDate"
                            @keypress="isNumber($event, true)"
                            @keyup.enter="!validateDate && form.edxIssueDate !== item.edxIssueDate && open()"
                            @blur.prevent="!validateDate && form.edxIssueDate !== item.edxIssueDate && open()"
                          />
                        </template>
                        <template #question>
                          Amending the Issue Date will also remove this record from Instance of Advice #{{
                            ioaCurrentItem + 1
                          }}
                        </template>
                      </ConfirmPopover>
                      <input
                        v-else
                        id="issueDate"
                        v-model.trim="form.edxIssueDate"
                        :class="validateDate"
                        type="text"
                        class="tw-form-input"
                        placeholder="YYYY MM DD"
                        maxlength="10"
                        @keypress="isNumber($event, true)"
                        @keyup.enter="!validateDate && onDateTimeSelected()"
                        @blur.prevent="!validateDate && onDateTimeSelected()"
                      />
                    </template>
                    <template v-if="item.edxIssueDate && canAnnotate">
                      <ConfirmPopover
                        v-if="ioaCurrentItem > -1"
                        trigger="manual"
                        confirm-text="Proceed"
                        :trigger-props="{ asChild: true }"
                        @confirmed="removeIssueDate"
                      >
                        <template #trigger="{ open }">
                          <StringTooltip item="Delete" #="{ item }">
                            <button type="button" :aria-label="item" class="remove-icon" @click="open">
                              <i aria-hidden="true" class="fa fa-trash tw-text-red-700" />
                            </button>
                          </StringTooltip>
                        </template>
                        <template #question>
                          Deleting the Issue Date will also remove this record from Instance of Advice #{{
                            ioaCurrentItem + 1
                          }}
                        </template>
                      </ConfirmPopover>
                      <StringTooltip v-else item="Delete" #="{ item }">
                        <button type="button" :aria-label="item" class="remove-icon">
                          <i aria-hidden="true" class="fa fa-trash tw-text-red-700" @click.prevent="removeIssueDate" />
                        </button>
                      </StringTooltip>
                    </template>
                    <div id="issueDate-feedback" class="tw-mt-1 tw-text-red-700 empty:tw-hidden">
                      {{ form.edxIssueDateMessage }}
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div class="tw-mb-2 tw-mr-3 tw-bg-white tw-px-2 tw-pt-2 tw-shadow">
              <div class="tw-mb-1 tw-grid tw-grid-cols-1">
                <label for="classificationGroupPicker">Classification Group</label>
                <div>
                  <p v-if="!canClassify" class="tw-form-input">
                    {{ form.aiClassificationGroup }}
                  </p>
                  <select
                    v-else
                    id="classificationGroupPicker"
                    v-model="form.aiClassificationGroup"
                    class="tw-form-select"
                    @change.prevent="onClassificationGroupChanged"
                  >
                    <option
                      v-for="(classificationGroup, index) in documentClassificationsGroup"
                      :key="`classification-group-${index}`"
                      :value="classificationGroup"
                    >
                      {{ classificationGroup }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="tw-mb-1 tw-grid tw-grid-cols-1">
                <label for="aiClassification">Classification</label>
                <div>
                  <p v-if="!canClassify" class="tw-form-input">
                    {{ form.aiClassification }}
                  </p>
                  <select
                    v-else
                    id="aiClassification"
                    v-model="form.aiClassification"
                    class="tw-form-select"
                    @change.prevent="onClassificationChanged"
                  >
                    <option
                      v-for="(classification, index) in documentClassifications"
                      :key="`classification-${index}`"
                      :value="classification"
                    >
                      {{ classification }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
            <div class="tw-mb-2 tw-mr-3 tw-bg-white tw-pt-2 tw-shadow">
              <div v-if="form.edxIssueDate && !ioaConfig.disabled" class="tw-mx-2 tw-mb-1 tw-grid tw-grid-cols-1">
                <label for="ioa">{{ ioaConfig.label }}</label>
                <div>
                  <select
                    v-if="ioaCurrentItem < 0"
                    id="ioa"
                    v-can:annotate
                    class="tw-b-r tw-w-full tw-rounded-md tw-px-2 tw-py-1"
                    :disabled="ioaStatus !== 'Ready'"
                    @change.prevent="addToIoA(($event.target as HTMLSelectElement).value)"
                  >
                    <option default>Select {{ ioaConfig.label }}</option>
                    <option
                      v-for="option in ioaOptions"
                      :key="option.instanceReference"
                      :value="option.instanceReference"
                    >
                      {{ option.message }}
                    </option>
                  </select>
                  <button
                    v-else-if="$can('annotate')"
                    class="hover:tw-opacity-75"
                    title="click to remove"
                    :disabled="ioaStatus !== 'Ready'"
                    @click.prevent="deleteFromIoA"
                  >
                    [<strong>#{{ ioaCurrentItem + 1 }}</strong
                    >]
                  </button>
                  <template v-else-if="ioaCurrentItem >= 0">
                    [<strong>#{{ ioaCurrentItem + 1 }}</strong
                    >]
                  </template>
                </div>
              </div>
              <div class="tw-mb-1">
                <RsManageTags primary-mode="tagFiles" />
              </div>
            </div>
            <div class="tw-mb-2 tw-mr-3 tw-px-2 tw-pt-2">
              <div v-if="propertiesConfig?.duplicateCheckbox?.disabled !== true">
                <RsCheckbox id="isDuplicate" v-model="form.isDuplicate" disabled>Is Duplicate?</RsCheckbox>
              </div>
              <div v-if="propertiesConfig?.draftCheckbox?.disabled !== true">
                <RsCheckbox id="isDraft" v-model="form.isDraft" :disabled="!canAnnotate" @change="onDraftChange"
                  >Is Draft?</RsCheckbox
                >
              </div>
            </div>
            <div class="tw-my-2 tw-flex tw-w-full tw-self-start">
              <button
                v-if="downloadState !== 'Form'"
                v-can:download
                class="tw-bg-gray active tw-b-r tw-mr-2 tw-rounded-md tw-px-2 tw-py-1 tw-text-xs"
                @click="downloadEarliest(item)"
              >
                Download {{ downloadState }}
              </button>
              <button
                v-can:ViewAudit
                class="tw-bg-gray active tw-b-r tw-rounded-md tw-px-2 tw-py-1 tw-text-xs"
                @click.prevent="viewAudit"
              >
                Record Audit
              </button>
            </div>
          </div>
        </TabsContent>
        <!-- Office properties goes after here -->
        <TabsContent as-child value="officeProperties">
          <fieldset class="p-3 tw-overflow-auto">
            <template v-for="(value, key) in extendedMetadata?.officeProperties" :key="key">
              <div class="d-flex flex-row flex-nowrap justify-content-between">
                <div class="mb-1 mr-3" :title="key.toString()">
                  <strong>{{ key }}</strong>
                </div>
                <div class="mb-1 text-nowrap text-truncate" :title="value">
                  {{ value }}
                </div>
              </div>
            </template>
          </fieldset>
        </TabsContent>
        <TabsContent as-child value="officeCustomProperties">
          <fieldset class="p-4 tw-overflow-auto">
            <template v-for="(value, key) in extendedMetadata?.officeCustomProperties" :key="key">
              <div class="d-flex flex-row flex-nowrap justify-content-between">
                <div class="mb-1 mr-3" :title="key.toString()">
                  <strong>{{ key }}</strong>
                </div>
                <div class="mb-1 text-nowrap text-truncate" :title="value">
                  {{ value }}
                </div>
              </div>
            </template>
          </fieldset>
        </TabsContent>
        <TabsContent as-child value="identities">
          <RsManageIdentities :file="item" />
        </TabsContent>
        <TabsContent as-child value="sha">
          <RecordSHAVerification :file="item" />
        </TabsContent>
      </TabsRoot>
    </div>
    <div v-else class="tw-grid tw-size-full tw-place-items-center">
      <div class="tw-max-w-sm tw-text-pretty">No file selected</div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.vdatetime {
  width: 100%;
}
.btn-info {
  background-color: #f79420;
}
.active-tab {
  text-decoration: underline;
}
.metadata {
  background: #ffffff;
}

.icon {
  cursor: pointer;
}
.wrapped {
  white-space: normal;
}
.p-2 .form-row > [class*="col-"] {
  max-width: 300px;
}

.remove-icon {
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
  cursor: pointer;
  @apply tw-aspect-square tw-size-7 tw-rounded-full hover:tw-bg-red-200/30;
}
</style>
